import React from 'react';
import { Loader, Switch, TextInput } from '@mantine/core';
import { Button } from '../../../../components/Button';
import type { ProductView } from '../../../../services/products/products.service';
import { noSelectionTextInputProps } from '../../../PosShellPage/components/receipt/receipt-input.utils';
import { useTenbisMenuSection } from './useTenbisMenuSection';
import classes from '../../monitoring-page.module.scss';

type TenbisMenuSectionProps = {
  tenantId: string | null;
  employeeSession: string | null;
};

type CatalogIdFieldProps = {
  product: ProductView;
  disabled: boolean;
  onSave: (product: ProductView, raw: string) => Promise<void>;
};

// Editable because existing 10bis menus carry foreign CatalogIds; saved on blur/Enter, diff-only.
const CatalogIdField: React.FC<CatalogIdFieldProps> = ({ product, disabled, onSave }) => {
  const [draft, setDraft] = React.useState(product.tenbisCatalogId ?? '');
  React.useEffect(() => {
    setDraft(product.tenbisCatalogId ?? '');
  }, [product.tenbisCatalogId]);
  return (
    <TextInput
      size="xs"
      value={draft}
      placeholder="p{id} אוטומטי"
      disabled={disabled}
      {...noSelectionTextInputProps}
      onChange={(event) => setDraft(event.currentTarget.value)}
      onBlur={() => {
        void onSave(product, draft);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        }
      }}
      data-testid={`monitoring-tenbis-menu-catalog-id-${product.id}`}
    />
  );
};

// Mapping surface inside מעקב תן ביס: include toggle + editable CatalogId + full-replace sync.
export const TenbisMenuSection: React.FC<TenbisMenuSectionProps> = ({ tenantId, employeeSession }) => {
  const section = useTenbisMenuSection({ tenantId, employeeSession });

  if (!section.tenant || !section.tenbisConnected) {
    return (
      <div className={classes.emptyState} data-testid="monitoring-tenbis-menu-disconnected">
        {'תן ביס אינו מחובר לעסק הנבחר. יש להשלים חיבור במסך ניהול חברות.'}
      </div>
    );
  }

  return (
    <div className={classes.sectionStack} data-testid="monitoring-tenbis-menu">
      <div className={classes.integrationMenuHeader}>
        <h3 className={classes.sectionTitle}>{'תפריט תן ביס'}</h3>
        <Button
          variant="save"
          size="sm"
          loading={section.isSyncing}
          loadingLabel="מסנכרן..."
          onClick={section.syncMenu}
          dataTestId="monitoring-tenbis-menu-sync"
        >
          {'סנכרן תפריט'}
        </Button>
      </div>
      {!section.canEdit ? (
        <div className={classes.emptyState} data-testid="monitoring-tenbis-menu-no-session">
          {'לעריכת המוצרים בתפריט יש להתחבר כמנהל לעסק זה במסך הקופה.'}
        </div>
      ) : section.isLoading && section.products.length === 0 ? (
        <div className={classes.loadingWrap}>
          <Loader />
        </div>
      ) : (
        <div className={classes.tableWrap}>
          {section.error && <p className={classes.error}>{section.error}</p>}
          <table className={classes.table} data-testid="monitoring-tenbis-menu-table">
            <thead>
              <tr>
                <th>{'מוצר'}</th>
                <th>{'מחיר'}</th>
                <th>{'מזהה בתן ביס'}</th>
                <th>{'כלול בתפריט'}</th>
              </tr>
            </thead>
            <tbody>
              {section.products.map((product) => {
                const saving = section.savingIds.includes(product.id);
                return (
                  <tr key={product.id} className={product.isActive ? undefined : classes.mutedRow} data-testid={`monitoring-tenbis-menu-row-${product.id}`}>
                    <td data-label={'מוצר'}>{product.name}</td>
                    <td data-label={'מחיר'}>{`${product.price.toFixed(1)} ₪`}</td>
                    <td data-label={'מזהה בתן ביס'}>
                      <CatalogIdField product={product} disabled={saving} onSave={section.saveCatalogId} />
                    </td>
                    <td data-label={'כלול בתפריט'}>
                      <Switch
                        checked={Boolean(product.tenbisMenuIncluded)}
                        disabled={saving}
                        onChange={() => {
                          void section.toggleIncluded(product);
                        }}
                        data-testid={`monitoring-tenbis-menu-include-${product.id}`}
                      />
                    </td>
                  </tr>
                );
              })}
              {section.products.length === 0 && (
                <tr>
                  <td colSpan={4} className={classes.empty}>{'אין מוצרים לעסק זה.'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
